import { ExternalLink } from "lucide-react";
import { BillingModeBadge } from "@/components/clients/ClientBadges";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useBillingInvoices } from "@/hooks/useBillingInvoices";
import { useBillingPortal } from "@/hooks/useBillingPortal";
import { useBillingSummary } from "@/hooks/useBillingSummary";

const formatAmount = (total: number, currency: string) =>
	new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: currency.toUpperCase(),
	}).format(total / 100);

export const BillingPage = () => {
	const { data: summary, isLoading: summaryLoading } = useBillingSummary();
	const { data: invoices, isLoading: invoicesLoading } = useBillingInvoices();
	const billingPortal = useBillingPortal();

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between">
				<h1 className="text-2xl font-bold text-white">Billing</h1>
				<Button
					onClick={() => billingPortal.openPortal()}
					disabled={billingPortal.isPending}
				>
					<ExternalLink className="h-4 w-4" />
					{billingPortal.isPending ? "Opening..." : "Manage billing"}
				</Button>
			</div>

			<Card className="bg-clyr-gray-800 rounded-lg border-clyr-gray-700">
				<CardContent className="p-4">
					{summaryLoading ? (
						<Skeleton className="h-14 w-full" />
					) : (
						<div className="grid grid-cols-3 gap-4 text-sm">
							<div>
								<div className="text-xs uppercase text-clyr-gray-400 mb-1">
									Billing mode
								</div>
								<BillingModeBadge mode={summary?.mode ?? null} />
							</div>
							<div>
								<div className="text-xs uppercase text-clyr-gray-400 mb-1">
									Subscription status
								</div>
								<div className="text-white capitalize">
									{summary?.subscription?.status ?? "No subscription"}
								</div>
							</div>
							<div>
								<div className="text-xs uppercase text-clyr-gray-400 mb-1">
									Current plan
								</div>
								<div className="text-white">
									{summary?.subscription?.currentPlan ?? "—"}
								</div>
							</div>
						</div>
					)}
				</CardContent>
			</Card>

			<h2 className="text-lg font-semibold text-white pt-2">Invoices</h2>
			<div className="bg-clyr-gray-800 rounded-lg border border-clyr-gray-700 overflow-hidden">
				<Table>
					<TableHeader>
						<TableRow className="border-clyr-gray-700 text-xs uppercase">
							<TableHead className="text-clyr-gray-400">Number</TableHead>
							<TableHead className="text-clyr-gray-400">Status</TableHead>
							<TableHead className="text-right text-clyr-gray-400">
								Total
							</TableHead>
							<TableHead className="text-clyr-gray-400">Date</TableHead>
							<TableHead className="text-clyr-gray-400" />
						</TableRow>
					</TableHeader>
					<TableBody>
						{invoicesLoading ? (
							<TableRow>
								<TableCell
									colSpan={5}
									className="py-8 text-center text-clyr-gray-500"
								>
									Loading...
								</TableCell>
							</TableRow>
						) : !invoices?.length ? (
							<TableRow>
								<TableCell
									colSpan={5}
									className="py-8 text-center text-clyr-gray-500"
								>
									No invoices
								</TableCell>
							</TableRow>
						) : (
							invoices.map((invoice) => (
								<TableRow key={invoice.id} className="border-clyr-gray-700/50">
									<TableCell className="text-white font-medium">
										{invoice.number ?? invoice.id}
									</TableCell>
									<TableCell className="text-clyr-gray-400 capitalize">
										{invoice.status ?? "—"}
									</TableCell>
									<TableCell className="text-right text-clyr-gray-300 tabular-nums">
										{formatAmount(invoice.total, invoice.currency)}
									</TableCell>
									<TableCell className="text-clyr-gray-400">
										{new Date(invoice.created).toLocaleDateString()}
									</TableCell>
									<TableCell className="text-right">
										{invoice.hostedInvoiceUrl && (
											<a
												href={invoice.hostedInvoiceUrl}
												target="_blank"
												rel="noreferrer"
												className="text-clyr-blue-400 hover:text-clyr-blue-300 text-sm inline-flex items-center gap-1"
											>
												View
												<ExternalLink className="h-3 w-3" />
											</a>
										)}
									</TableCell>
								</TableRow>
							))
						)}
					</TableBody>
				</Table>
			</div>
		</div>
	);
};
